import api from './api';

export const groupApplicationService = {
    // Submit a new group application
    submitApplication: async (userId, groupId, householdId, reason) => {
        try {
            const response = await api.post('/group-applications', {
                userId,
                groupId,
                householdId,
                reason
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to submit application');
        }
    },

    // Get all applications (admin only)
    getAllApplications: async () => {
        try {
            const response = await api.get('/group-applications');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get applications');
        }
    },

    // Get pending applications
    getPendingApplications: async () => {
        try {
            const response = await api.get('/group-applications/pending');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get pending applications');
        }
    },

    // Get applications by user
    getApplicationsByUser: async (userId) => {
        try {
            const response = await api.get(`/group-applications/user/${userId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get user applications');
        }
    },

    // Get applications by group
    getApplicationsByGroup: async (groupId) => {
        try {
            const response = await api.get(`/group-applications/group/${groupId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get group applications');
        }
    },

    // Get applications by household
    getApplicationsByHousehold: async (householdId) => {
        try {
            const response = await api.get(`/group-applications/household/${householdId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get household applications');
        }
    },

    // Get applications that need team supervisor review
    getApplicationsNeedingTeamSupervisorReview: async () => {
        try {
            const response = await api.get('/group-applications/needing-team-supervisor-review');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get applications needing team supervisor review');
        }
    },

    // Get applications that need coach/staff review
    getApplicationsNeedingCoachStaffReview: async () => {
        try {
            const response = await api.get('/group-applications/needing-coach-staff-review');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get applications needing coach/staff review');
        }
    },

    // Get applications for teams supervised by a specific user
    getApplicationsForTeamsSupervisedBy: async (teamSupervisorId) => {
        try {
            const response = await api.get(`/group-applications/teams-supervised-by/${teamSupervisorId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get applications for supervised teams');
        }
    },

    // Check if user has pending application for a group
    checkPendingApplication: async (userId, groupId) => {
        try {
            const response = await api.get('/group-applications/check-pending', {
                params: { userId, groupId }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to check pending application');
        }
    },

    // Team Supervisor approval/rejection
    reviewByTeamSupervisor: async (applicationId, reviewerId, approved, notes) => {
        try {
            const response = await api.post(`/group-applications/${applicationId}/team-supervisor-review`, {
                reviewerId,
                approved,
                notes
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to review application');
        }
    },

    // Coach/Staff approval/rejection
    reviewByCoachStaff: async (applicationId, reviewerId, approved, notes) => {
        try {
            const response = await api.post(`/group-applications/${applicationId}/coach-staff-review`, {
                reviewerId,
                approved,
                notes
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to review application');
        }
    },

    // Get applications for teams seeking members in a specific league
    getApplicationsForSeekingTeamsInLeague: async (householdId, groupTypeId) => {
        try {
            const response = await api.get('/group-applications/seeking-teams-in-league', {
                params: { householdId, groupTypeId }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get applications for seeking teams');
        }
    },

    // Submit a team invitation (team supervisor inviting student)
    submitTeamInvitation: async (userId, groupId, householdId, reason, teamSupervisorId) => {
        try {
            const response = await api.post('/group-applications/invite', {
                userId,
                groupId,
                householdId,
                reason,
                teamSupervisorId
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to submit team invitation');
        }
    },

    // Submit a supervisor invitation (team supervisor inviting another supervisor)
    submitSupervisorInvitation: async (userId, groupId, householdId, reason, teamSupervisorId) => {
        try {
            const response = await api.post('/group-applications/invite-supervisor', {
                userId,
                groupId,
                householdId,
                reason,
                teamSupervisorId
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to submit supervisor invitation');
        }
    },

    // Get team invitations for a household
    getTeamInvitationsForHousehold: async (householdId) => {
        try {
            const response = await api.get(`/group-applications/household/${householdId}/invitations`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get team invitations');
        }
    },

    // Get team invitations that need primary caregiver review
    getTeamInvitationsNeedingPrimaryCaregiverReview: async () => {
        try {
            const response = await api.get('/group-applications/needing-primary-caregiver-review');
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to get team invitations needing review');
        }
    },

    // Primary Caregiver approval/rejection for team invitations
    reviewByPrimaryCaregiver: async (applicationId, reviewerId, approved, notes) => {
        try {
            const response = await api.post(`/group-applications/${applicationId}/primary-caregiver-review`, {
                reviewerId,
                approved,
                notes
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to review team invitation');
        }
    }
}; 